import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Heading, HStack, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, Table, Tbody, Td, Th, Thead, Tr, useDisclosure, useToast, Textarea } from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon, RepeatIcon, LinkIcon } from "@chakra-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import rbacService from "../services/rbacService";
import type { Role, Application } from "../../rbac/types";

const RolesPage: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<Role[]>([]);
  const [apps, setApps] = useState<Application[]>([]);
  const [search, setSearch] = useState("");

  const [editing, setEditing] = useState<Role | null>(null);
  const [form, setForm] = useState<{ app_id: string | ""; role_name: string; description: string; }>({ app_id: "", role_name: "", description: "" });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(r => r.role_name.toLowerCase().includes(q) || (r.description || "").toLowerCase().includes(q));
  }, [items, search]);

  const load = async () => {
    setLoading(true);
    try {
      const [roles, applications] = await Promise.all([
        rbacService.getRoles({ skip: 0, limit: 1000 }),
        rbacService.getApplications({ skip: 0, limit: 1000 }),
      ]);
      setItems(roles);
      setApps(applications);
    } catch (e: any) {
      toast({ status: "error", title: "Gagal memuat data", description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ app_id: "", role_name: "", description: "" });
    onOpen();
  };

  const openEdit = (row: Role) => {
    setEditing(row);
    setForm({ app_id: row.app_id ?? "", role_name: row.role_name, description: row.description || "" });
    onOpen();
  };

  const submit = async () => {
    try {
      const payload = { role_name: form.role_name, description: form.description, app_id: form.app_id === "" ? null : form.app_id };
      if (editing) {
        await rbacService.updateRole(editing.id, payload as any);
        toast({ status: "success", title: "Role diperbarui" });
      } else {
        await rbacService.createRole(payload as any);
        toast({ status: "success", title: "Role dibuat" });
      }
      onClose();
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Operasi gagal", description: e?.response?.data?.detail || e?.message });
    }
  };

  const remove = async (row: Role) => {
    if (!window.confirm(`Hapus role '${row.role_name}'? Ini akan menghapus semua assignment.`)) return;
    try {
      await rbacService.deleteRole(row.id);
      toast({ status: "success", title: "Role dihapus" });
      await load();
    } catch (e: any) {
      toast({ status: "error", title: "Gagal menghapus", description: e?.response?.data?.detail || e?.message });
    }
  };

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="md">Roles</Heading>
        <HStack>
          <IconButton aria-label="refresh" icon={<RepeatIcon />} onClick={() => load()} isLoading={loading} />
          <Button leftIcon={<AddIcon />} colorScheme="green" onClick={openCreate}>New</Button>
        </HStack>
      </Flex>

      <HStack mb={3}>
        <Input placeholder="Cari role..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </HStack>

      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>App</Th>
            <Th>Role</Th>
            <Th>description</Th>
            <Th isNumeric>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filtered.map((row) => (
            <Tr key={row.id}>
              <Td>{row.id}</Td>
              <Td>{row.app_id == null ? "(Global)" : apps.find(a => a.id === row.app_id)?.app_name || row.app_id}</Td>
              <Td>{row.role_name}</Td>
              <Td>{row.description}</Td>
              <Td isNumeric>
                <HStack justify="flex-end">
                  <IconButton aria-label="edit" icon={<EditIcon />} onClick={() => openEdit(row)} size="sm" />
                  <IconButton aria-label="delete" icon={<DeleteIcon />} onClick={() => remove(row)} colorScheme="red" size="sm" />
                  <Button as={RouterLink} to={`/admin/roles/${row.id}`} leftIcon={<LinkIcon />} size="sm" variant="outline">Menus</Button>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editing ? "Edit Role" : "Create Role"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Select placeholder="Global (kosongkan) atau pilih aplikasi" value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value ? e.target.value : "" })} mb={3}>
              <option value="">Global (tidak terikat app)</option>
              {apps.map(a => (
                <option key={a.id} value={a.id}>{a.app_name}</option>
              ))}
            </Select>
            <Input placeholder="role_name" value={form.role_name} onChange={(e) => setForm({ ...form, role_name: e.target.value })} mb={3} />
            <Textarea placeholder="description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="green" onClick={submit} isDisabled={!form.role_name.trim()}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default RolesPage;
